import {
  Box,
  FormControlLabel,
  Grid,
  InputLabel,
  Typography,
  TextField,
  MenuItem,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import AnimatedButton from "components/custom/AnimatedButton";
import { COUNTRY } from "config";
import { setLoaderText } from "store/features/loaderSlice";
import {
  setLoadingValue,
  setPurchase,
  setBindResponse,
} from "store/features/paymentSlice";
import { updatePurchaseAPI, bindQuote, generateQuotesAPI, createPurchase } from "api/siaApplication";
import {
  customStylesSelector,
  paymentSelector,
} from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";
import PaymentFooter from "./PaymentFooter";
import { toggleAutoRenewalConsent, setErrorAndShowExitBtn } from "store/features/clientSlice";
import { nextStep, setShowPaymentScreen } from "store/features/stepperSlice";
import { setAvailableQuotes, addOrRemoveQuote } from "store/features/quotesSlice";
import RenewalForm from "./RenewalForm";
import { sendMessageToClient } from "utils/utils";
import PopupModal from "components/common/PopupModal/PopupModal";
import { downloadFile } from "utils/file";
import {
  validateCreditCard,
  validateExpiryDate,
  validateCVCLength,
  detectCardBrand,
  validateCPF,
  validateCNPJ,
} from "utils/creditCardValidation";

export type CustomPaymentProps = {
  customerEmail?: string;
  onCancel: () => void;
  onSubmit: (details: any) => Promise<void>;
  paymentData?: PaymentPayload[];
  showAutoRenew?: boolean;
};

enum PaymentMethod {
  CREDIT_CARD = "CREDIT_CARD",
  ACCOUNT_DEBIT = "ACCOUNT_DEBIT",
}

const CustomPayment: React.FC<CustomPaymentProps> = ({
  customerEmail,
  onSubmit,
  onCancel,
  paymentData,
  showAutoRenew = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useT();
  const customStyles = useSelector(customStylesSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.CREDIT_CARD
  );

  const { clientAPIKey, clientUrl } = useSelector((state: RootState) => state.client);
  const { purchase } = useSelector((state: RootState) => state.payment);
  const { bindResponse } = useSelector(paymentSelector);
  const { selectedQuotes, availableQuotes } = useSelector((state: RootState) => state.quotes);
  const { submissionId, updatedSiaApplication } = useSelector((state: RootState) => state.surveyJs);

  const productSelected = selectedQuotes[0]?.product;
  const isAutoAllianz = productSelected === "AUTO-ALLIANZ";
  const isMotoAllianz = productSelected === "MOTO-ALLIANZ";
  const inspectionData = bindResponse?.response?.inspectionResponse;

  const isBindFailed = bindResponse?.status === "bind-failed";

  // Get homeowners payment method from application_params
  const homeownersPaymentMethod = updatedSiaApplication?.params?.homeowners_payment_method;

  // Helper function to check for duplicate proposal error
  const isDuplicateProposalError = () => {
    if (!bindResponse?.response?.errors) return false;
    
    return bindResponse.response.errors.some((error: any) => 
      error.descricao?.toLowerCase().includes('duplicidade') ||
      error.descricao?.toLowerCase().includes('duplicate')
    );
  };

  const getDuplicateProposalNumber = () => {
    if (!bindResponse?.response?.errors) return null;
    
    const duplicateError = bindResponse.response.errors.find((error: any) => 
      error.descricao?.toLowerCase().includes('duplicidade') ||
      error.descricao?.toLowerCase().includes('duplicate')
    );
    
    // Extract proposal number from message like "Veículo em duplicidade com proposta 121237356"
    const match = duplicateError?.descricao?.match(/proposta\s+(\d+)/i);
    return match ? match[1] : null;
  };

  const getBlockedProposalNumber = () => {
    return bindResponse?.response?.apolice?.numProposta ?? null;
  };

  // Get modal content based on error type
  const getModalContent = () => {
    if (isDuplicateProposalError()) {
      const proposalNumber = getDuplicateProposalNumber();
      return {
        title: "Proposta Duplicada",
        content: `Este veículo já possui uma proposta ativa no sistema${
          proposalNumber ? ` (Proposta nº ${proposalNumber})` : ''
        }. Para prosseguir, você precisa utilizar uma placa diferente para gerar uma nova cotação.`,
        btnText: "Entendi, usar placa diferente",
        onSave: () => {} // Just close modal for duplicate proposals
      };
    }
    
    // Get payment type from payment_collection_info
    const paymentType = purchase?.payment_collection_info?.type;
    const message = paymentType === "debit"
      ? t("PAYMENT_RETRY_DIALOG_MESSAGE_DEBIT")
      : t("PAYMENT_RETRY_DIALOG_MESSAGE");
    const blockedProposalNumber = getBlockedProposalNumber();
    const content = blockedProposalNumber
      ? `${message} (Proposta nº ${blockedProposalNumber})`
      : message;
    
    return {
      title: "Confirmação de Pagamento",
      content: content,
      btnText: t("PAYMENT_RETRY_DIALOG_CONFIRM"),
      onSave: executeRetryPayment
    };
  };

  const handleRetryPayment = () => {
    // Get payment type from payment_collection_info
    const paymentType = purchase?.payment_collection_info?.type;
    
    // For debit cards, automatically execute retry without showing modal
    if (paymentType === "debit") {
      executeRetryPayment();
    } else {
      // For other payment types, show the confirmation modal
      setShowRetryModal(true);
    }
  };

  const executeRetryPayment = async () => {
    try {
      setIsLoading(true);
      dispatch(setLoadingValue(true));
      dispatch(setLoaderText(t("LOADING_PAYMENT_WIDGET")));

      const failedQuote = selectedQuotes[0];
      const failedPlanName = failedQuote?.premium?.additional_info?.plan_name?.default;

      const quotesRes = await generateQuotesAPI(clientAPIKey, submissionId, true);

      if (
        quotesRes?.data?.status === "generated_quotes" &&
        quotesRes?.data?.quotes?.length
      ) {
        const filteredQuotes = (quotesRes?.data?.quotes || []).filter(
          (q) => q.status === "generated"
        );

        const matchingQuote = filteredQuotes.find(
          (quote) => quote.premium?.additional_info?.plan_name?.default === failedPlanName
        );

        if (matchingQuote) {
          dispatch(setAvailableQuotes(filteredQuotes));
          dispatch(addOrRemoveQuote(matchingQuote));
          const purchaseRes = await createPurchase(clientAPIKey, {
            for_type: "quote",
            for_ids: [matchingQuote.id],
            auto_renewal_consent: false,
          });

          dispatch(setPurchase(purchaseRes.data));
          setIsPaymentFailed(false);
        } else {
          dispatch(setErrorAndShowExitBtn(true));
        }
      } else {
        dispatch(setErrorAndShowExitBtn(true));
      }
    } catch (error) {
      dispatch(setErrorAndShowExitBtn(true));
    } finally {
      setIsLoading(false);
      dispatch(setLoadingValue(false));
      dispatch(setLoaderText(null));
    }
  };

  const handleExit = () => {
    sendMessageToClient({ type: "EXIT", details: {} }, clientUrl);
  };

  const TIPO_LOGRADOURO_OPTIONS = [
    { value: "R.", label: "Rua" },
    { value: "AE", label: "Área Especial" },
    { value: "AL", label: "Alameda" },
    { value: "AV", label: "Avenida" },
    { value: "AR", label: "Área" },
    { value: "CH", label: "Chácara" },
    { value: "CJ", label: "Conjunto" },
    { value: "CL", label: "Colônia" },
    { value: "CM", label: "Caminho" },
    { value: "CO", label: "Condomínio" },
    { value: "DT", label: "Distrito" },
    { value: "EP", label: "Esplanada" },
    { value: "ES", label: "Estrada" },
    { value: "ET", label: "Estação" },
    { value: "FE", label: "Feira" },
    { value: "FV", label: "Favela" },
    { value: "FZ", label: "Fazenda" },
    { value: "JD", label: "Jardim" },
    { value: "LA", label: "Ladeira" },
    { value: "LD", label: "Lado" },
    { value: "LO", label: "Lote" },
    { value: "LG", label: "Largo" },
    { value: "LT", label: "Loteamento" },
    { value: "MO", label: "Módulo" },
    { value: "NU", label: "Núcleo" },
    { value: "OU", label: "Outros" },
    { value: "PC", label: "Praça" },
    { value: "PQ", label: "Parque" },
    { value: "PS", label: "Passagem" },
    { value: "PT", label: "Pátio" },
    { value: "QD", label: "Quadra" },
    { value: "RC", label: "Recreio" },
    { value: "RD", label: "Rodovia" },
    { value: "RS", label: "Residencial" },
    { value: "S.", label: "Setor" },
    { value: "ST", label: "Sítio" },
    { value: "TC", label: "Travessa Particular" },
    { value: "TV", label: "Travessa" },
    { value: "TO", label: "Trecho" },
    { value: "V.", label: "Via" },
    { value: "VD", label: "Viaduto" },
    { value: "VI", label: "Vila" },
    { value: "VL", label: "Vale" },
    { value: "VE", label: "Vereda" },
    { value: "VR", label: "Viela" },
  ];

  const TIPO_ENDERECO_OPTIONS = [
    { value: "RESIDENCIAL", label: "Residencial" },
    { value: "COMERCIAL", label: "Comercial" },
  ];

  const CONTA_TIPO_OPTIONS = [
    { value: "CONTA_CORRENTE", label: "Conta Corrente" },
    { value: "CONTA_POUPANCA", label: "Conta Poupança" },
  ];

  const PARENTESCO_OPTIONS = [
    { value: "1", label: "Titular" },
    { value: "2", label: "Companheiro(a)" },
    { value: "3", label: "Cônjuge" },
    { value: "4", label: "Irmã(o)" },
    { value: "5", label: "Filho(a)" },
    { value: "6", label: "Mãe" },
    { value: "7", label: "Pai" },
  ];

  const ACCOUNT_RELATIONSHIP_OPTIONS = [
    { value: "0", label: "Titular" },
    { value: "2", label: "Companheiro(a)" },
    { value: "1", label: "Cônjuge" },
    { value: "7", label: "Irmão" },
    { value: "3", label: "Filho(a)" },
    { value: "5", label: "Mãe" },
    { value: "4", label: "Pai" },
  ];

  const GENDER_OPTIONS = [
    { value: "M", label: "Masculino" },
    { value: "F", label: "Feminino" },
  ];

  const HOME_ALLIANZ_BANK_OPTIONS = [
    { value: "0237", label: "0237 - BANCO BRADESCO S.A." },
    { value: "0033", label: "0033 - BANCO SANTANDER S.A." },
    { value: "0001", label: "0001 - BANCO DO BRASIL S.A." },
    { value: "0104", label: "0104 - CAIXA ECONOMICA FEDERAL" },
    { value: "0756", label: "0756 - BANCO COOPERATIVO DO BRASIL S.A." },
    { value: "0748", label: "0748 - BANCO COOPERATIVO SICREDI S.A. - BA" },
    { value: "0041", label: "0041 - BANCO DO ESTADO DO RIO GRANDE DO SU" },
    { value: "0004", label: "0004 - BANCO DO NORDESTE DO BRASIL S.A." },
    { value: "0047", label: "0047 - BANCO DO ESTADO DE SERGIPE S.A." },
    { value: "0070", label: "0070 - BRB - BANCO DE BRASILIA S.A." },
    { value: "0084", label: "0084 - SISPRIME DO BRASIL" },
    { value: "0085", label: "0085 - Banco Cecred" },
    { value: "0133", label: "0133 - CRESOL CONFEDERAÇÃO" },
    { value: "0260", label: "0260 - NU PAGAMENTOS S.A." },
    { value: "0341", label: "0341 - ITAU" },
    { value: "0422", label: "0422 - BANCO SAFRA S.A." },
    { value: "0991", label: "0991 - BANCO CREDICOAMO" },
    { value: "0992", label: "0992 - BANCO MONDIAL" },
  ];

  const AUTO_ALLIANZ_BANK_OPTIONS = [
    { value: "001", label: "0001 - BANCO DO BRASIL S.A." },
    { value: "033", label: "0033 - BANCO SANTANDER S.A." },
    { value: "047", label: "0047 - BANCO DO ESTADO DE SERGIPE S.A." },
    { value: "070", label: "0070 - BRB - BANCO DE BRASILIA S.A." },
    { value: "104", label: "0104 - CAIXA ECONOMICA FEDERAL" },
    { value: "237", label: "0237 - BANCO BRADESCO S.A." },
    { value: "341", label: "0341 - ITAU" },
    { value: "422", label: "0422 - BANCO SAFRA S.A." },
    { value: "756", label: "0756 - BANCO COOPERATIVO DO BRASIL S.A." },
    { value: "991", label: "0991 - BANCO CREDICOAMO" },
    { value: "748", label: "0748 - BANCO COOPERATIVO SICREDI S.A. - BA" }
  ];

  const MOTO_ALLIANZ_BANK_OPTIONS = [
    { value: "001", label: "0001 - BANCO DO BRASIL S.A." },
    { value: "033", label: "0033 - BANCO SANTANDER S.A." },
    { value: "047", label: "0047 - BANCO DO ESTADO DE SERGIPE S.A." },
    { value: "070", label: "0070 - BRB - BANCO DE BRASILIA S.A." },
    { value: "104", label: "0104 - CAIXA ECONOMICA FEDERAL" },
    { value: "237", label: "0237 - BANCO BRADESCO S.A." },
    { value: "341", label: "0341 - ITAU" },
    { value: "422", label: "0422 - BANCO SAFRA S.A." },
    { value: "756", label: "0756 - BANCO COOPERATIVO DO BRASIL S.A." },
    { value: "991", label: "0991 - BANCO CREDICOAMO" },
    { value: "748", label: "0748 - BANCO COOPERATIVO SICREDI S.A. - BA" }
  ];

  const isHomeownersAllianz = productSelected === "HOMEOWNERS-ALLIANZ";
  const shouldUseBankDropdown = isHomeownersAllianz || isAutoAllianz || isMotoAllianz;

  const getInitialValues = () => {
    let initialPaymentMethod = PaymentMethod.CREDIT_CARD;
    
    if (isHomeownersAllianz) {
      // For homeowners-allianz, use homeowners_payment_method to determine form
      // 1 = debit card form (ACCOUNT_DEBIT)
      // 4 = credit card form (CREDIT_CARD)
      if (homeownersPaymentMethod === 1) {
        initialPaymentMethod = PaymentMethod.ACCOUNT_DEBIT;
      } else if (homeownersPaymentMethod === 4) {
        initialPaymentMethod = PaymentMethod.CREDIT_CARD;
      } else {
        // Default to ACCOUNT_DEBIT if no valid value
        initialPaymentMethod = PaymentMethod.ACCOUNT_DEBIT;
      }
    }

    return {
      paymentMethod: initialPaymentMethod,

      card_owner_name: "",
      card_owner_cpf_cnpj: "",
      card_brand: "VISA",
      card_number: "",
      card_exp_month: "",
      card_exp_year: "",
      card_sec_code: "",
      installments: 1,

      nomeTitular: "",
      documentoTitular: "",
      parentesco: "1",
      bancoNumero: (isAutoAllianz || isMotoAllianz) ? "001" : "0756",
      agenciaNumero: "",
      agenciaDigito: "",
      contaTipo: "CONTA_CORRENTE",
      contaNumero: "",
      contaDigito: "",
      diaPagamento: "",

      ownerAccountBankRelationship: "",
      gender: "",
      dateOfBirth: "",

      tipoEndereco: "RESIDENCIAL",
      tipoLogradouro: "R.",
      logradouro: "",
      numero: "",
      cep: "",
      bairro: "",

      additional_renewal_info: {
        ci_code: "",
        end_date: "",
        policy_number: "",
      },
    };
  };

  const creditCardValidationSchema = Yup.object().shape({
    is_policyholder: Yup.boolean().required(
      "É obrigatório informar se o segurado é o titular do cartão"
    ),
    card_owner_name: Yup.string().required(
      "Nome do titular do cartão é obrigatório"
    ),
    card_owner_cpf_cnpj: Yup.string()
      .required("CPF/CNPJ é obrigatório")
      .test("cpf-cnpj-validation", "CPF/CNPJ inválido", function(value) {
        if (!value) return false;
        const cleanedValue = value.replace(/\D/g, '');
        if (cleanedValue.length === 11) {
          return validateCPF(cleanedValue);
        } else if (cleanedValue.length === 14) {
          return validateCNPJ(cleanedValue);
        }
        return this.createError({ 
          message: "CPF deve conter 11 dígitos ou CNPJ deve conter 14 dígitos" 
        });
      }),
    card_brand: Yup.string().required("Bandeira do cartão é obrigatória"),
    card_number: Yup.string()
      .required("Número do cartão é obrigatório")
      .test("card-validation", "Número do cartão inválido", function(value) {
        if (!value) return false;
        const cardBrand = this.parent.card_brand;
        const validation = validateCreditCard(value, cardBrand);
        if (!validation.isValid) {
          return this.createError({ message: validation.error || "Número do cartão inválido" });
        }
        return true;
      }),
    card_exp_month: Yup.string()
      .matches(/^(0[1-9]|1[0-2])$/, "Mês deve estar entre 01 e 12")
      .required("Mês de validade é obrigatório"),
    card_exp_year: Yup.string()
      .matches(/^\d{2}$/, "Ano deve conter 2 dígitos")
      .required("Ano de validade é obrigatório")
      .test("expiry-validation", "Cartão expirado ou data inválida", function(value) {
        if (!value) return false;
        const month = this.parent.card_exp_month;
        if (!month) return true; // Let month validation handle it
        return validateExpiryDate(month, value);
      }),
    card_sec_code: Yup.string()
      .required("Código de segurança é obrigatório")
      .test("cvc-validation", "Código de segurança inválido", function(value) {
        if (!value) return false;
        const cardBrand = this.parent.card_brand;
        if (!validateCVCLength(value, cardBrand)) {
          return this.createError({ 
            message: `Código de segurança deve ter ${cardBrand === 'AMEX' ? '4' : '3'} dígitos` 
          });
        }
        return true;
      }),
    ...((isAutoAllianz || isMotoAllianz) && {
      installments: Yup.number()
        .min(1, "Número de parcelas deve ser pelo menos 1")
        .max(11, "Número de parcelas deve ser no máximo 11")
        .required("Número de parcelas é obrigatório"),
    }),
    tipoEndereco: Yup.string().required("Tipo de endereço é obrigatório"),
    tipoLogradouro: Yup.string().required("Tipo de logradouro é obrigatório"),
    logradouro: Yup.string().required("Logradouro é obrigatório"),
    numero: Yup.string().required("Número é obrigatório"),
    cep: Yup.string()
      .matches(/^\d{8}$/, "CEP deve conter 8 dígitos")
      .required("CEP é obrigatório"),
    bairro: Yup.string().required("Bairro é obrigatório"),
    additional_renewal_info: Yup.object().when([], {
      is: () =>
        selectedQuotes &&
        selectedQuotes.length > 0 &&
        selectedQuotes[0]?.data?.allianzRenewal &&
        productSelected !== "MOTO-ALLIANZ",
      then: () =>
        Yup.object().shape({
          ci_code: Yup.string().required(
            "Código de identificação é obrigatório"
          ),
          end_date: Yup.string().required(
            "Data de fim de vigência é obrigatória"
          ),
          policy_number: Yup.string().required(
            "Número da apólice é obrigatório"
          ),
        }),
      otherwise: () =>
        Yup.object().shape({
          ci_code: Yup.string(),
          end_date: Yup.string(),
          policy_number: Yup.string(),
        }),
    }),
  });

  const accountDebitValidationSchema = Yup.object().shape({
    nomeTitular: Yup.string().required("Nome do titular é obrigatório"),
    documentoTitular: Yup.string()
      .required("CPF é obrigatório")
      .test("cpf-validation", "CPF inválido", function(value) {
        if (!value) return false;
        const cleanedValue = value.replace(/\D/g, '');
        if (cleanedValue.length !== 11) {
          return this.createError({ message: "CPF deve conter 11 dígitos" });
        }
        return validateCPF(cleanedValue);
      }),
    parentesco: Yup.string().required("Parentesco é obrigatório"),
    bancoNumero: Yup.string().required("Número do banco é obrigatório"),
    agenciaNumero: Yup.string().required("Número da agência é obrigatório"),
    agenciaDigito: Yup.string(),
    contaTipo: Yup.string().required("Tipo de conta é obrigatório"),
    contaNumero: Yup.string().required("Número da conta é obrigatório"),
    contaDigito: Yup.string().required("Dígito da conta é obrigatório"),
    diaPagamento: Yup.string()
      .matches(/^(0?[1-9]|[12][0-9]|3[01])$/, "Dia deve estar entre 1 e 31")
      .required("Dia do pagamento é obrigatório"),
    ...((isAutoAllianz || isMotoAllianz) && {
      installments: Yup.number()
        .min(1, "Número de parcelas deve ser pelo menos 1")
        .max(11, "Número de parcelas deve ser no máximo 11")
        .required("Número de parcelas é obrigatório"),
    }),
    // Additional fields validation for HOMEOWNERS-ALLIANZ
    ...(isHomeownersAllianz && {
      ownerAccountBankRelationship: Yup.string().required(
        "Relacionamento com o titular da conta é obrigatório"
      ),
      gender: Yup.string().required("Sexo é obrigatório"),
      dateOfBirth: Yup.string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Data de nascimento deve estar no formato YYYY-MM-DD")
        .required("Data de nascimento é obrigatória"),
    }),
    tipoEndereco: Yup.string().required("Tipo de endereço é obrigatório"),
    tipoLogradouro: Yup.string().required("Tipo de logradouro é obrigatório"),
    logradouro: Yup.string().required("Logradouro é obrigatório"),
    numero: Yup.string().required("Número é obrigatório"),
    cep: Yup.string()
      .matches(/^\d{8}$/, "CEP deve conter 8 dígitos")
      .required("CEP é obrigatório"),
    bairro: Yup.string().required("Bairro é obrigatório"),
    additional_renewal_info: Yup.object().when([], {
      is: () =>
        selectedQuotes &&
        selectedQuotes.length > 0 &&
        selectedQuotes[0]?.data?.allianzRenewal &&
        productSelected !== "MOTO-ALLIANZ",
      then: () =>
        Yup.object().shape({
          ci_code: Yup.string().required(
            "Código de identificação é obrigatório"
          ),
          end_date: Yup.string().required(
            "Data de fim de vigência é obrigatória"
          ),
          policy_number: Yup.string().required(
            "Número da apólice é obrigatório"
          ),
        }),
      otherwise: () =>
        Yup.object().shape({
          ci_code: Yup.string(),
          end_date: Yup.string(),
          policy_number: Yup.string(),
        }),
    }),
  });

  const validationSchema = Yup.lazy((values) => {
    return values.paymentMethod === PaymentMethod.CREDIT_CARD
      ? creditCardValidationSchema
      : accountDebitValidationSchema;
  });

  const _onSubmit = async (values: any) => {
    try {
      dispatch(setLoadingValue(true));
      dispatch(setLoaderText(t("PROCESSING_PAYMENT")));
      setIsLoading(true);

      let paymentDetails;

      if (values.paymentMethod === PaymentMethod.CREDIT_CARD) {
        const cardNumber = values.card_number;
        const cnbr = btoa(cardNumber);

        paymentDetails = {
          type: "credit",
          dadosTitular: {
            nomeTitular: values.card_owner_name,
            documentoTitular: values.card_owner_cpf_cnpj || "07532221679",
            parentesco: values.parentesco,
          },
          contaTipo: values.contaTipo,
          card_acquirer: "CIELO_ECOMMERCE",
          card_owner_name: values.card_owner_name,
          card_brand: values.card_brand,
          card_number: cnbr,
          card_exp_month: values.card_exp_month,
          card_exp_year: values.card_exp_year,
          card_sec_code: values.card_sec_code,
          ...((isAutoAllianz || isMotoAllianz) && { installments: Number(values.installments) }),
          bairro: values.bairro,
          cep: values.cep,
          logradouro: values.logradouro,
          numero: Number(values.numero),
          tipoEndereco: values.tipoEndereco,
          tipoLogradouro: values.tipoLogradouro,
        };
      } else {
        paymentDetails = {
          type: "debit",
          dadosTitular: {
            nomeTitular: values.nomeTitular,
            documentoTitular: values.documentoTitular,
            parentesco: values.parentesco,
          },
          bancoNumero: values.bancoNumero,
          agenciaNumero: values.agenciaNumero,
          ...(values.agenciaDigito && { agenciaDigito: values.agenciaDigito }),
          contaTipo: values.contaTipo,
          contaNumero: values.contaNumero,
          contaDigito: values.contaDigito,
          diaPagamento: values.diaPagamento,
          ...((isAutoAllianz || isMotoAllianz) && { installments: Number(values.installments) }),
          bairro: values.bairro,
          cep: values.cep,
          logradouro: values.logradouro,
          numero: Number(values.numero),
          tipoEndereco: values.tipoEndereco,
          tipoLogradouro: values.tipoLogradouro,
          ...(isHomeownersAllianz && {
            ownerAccountBankRelationship: values.ownerAccountBankRelationship,
            gender: values.gender,
            dateOfBirth: values.dateOfBirth,
          }),
        };
      }

      if (
        selectedQuotes &&
        selectedQuotes.length > 0 &&
        selectedQuotes[0]?.data?.allianzRenewal
      ) {
        paymentDetails.additional_renewal_info = values.additional_renewal_info;
      }

      const updatedPurchaseRes = await updatePurchaseAPI(clientAPIKey, purchase.id, {
        auto_renewal_consent: false,
        payment_collection_info: paymentDetails,
      });


      dispatch(setPurchase(updatedPurchaseRes.data));

      if (selectedQuotes && selectedQuotes.length > 0) {
        const selectedQuote = selectedQuotes[0];
        const bindResponse: any = await bindQuote(
          clientAPIKey,
          selectedQuote.id
        );

        dispatch(setBindResponse(bindResponse?.data));

        if (
          bindResponse &&
          bindResponse?.data &&
          bindResponse?.data.status === "bind-failed"
        ) {
          if (bindResponse?.data?.response?.blockedAlerts) {
            setIsLoading(false);
            setIsPaymentFailed(false);
            const updatedPurchase = {
              ...purchase,
              status: "paid",
            };
            dispatch(setPurchase(updatedPurchase));
            dispatch(nextStep());
            dispatch(setShowPaymentScreen(false));
          } else {
            setIsPaymentFailed(true);
            return;
          }
        } else if (
          bindResponse &&
          bindResponse?.data &&
          bindResponse?.data.status === "bound"
        ) {
          setIsLoading(false);
          setIsPaymentFailed(false);
          const updatedPurchase = {
            ...purchase,
            status: "paid",
          };
          dispatch(setPurchase(updatedPurchase));
          dispatch(nextStep());
          dispatch(setShowPaymentScreen(false));
        }
      }
    } catch (error) {
      setIsPaymentFailed(true);
    } finally {
      setIsLoading(false);
      dispatch(setLoadingValue(false));
      dispatch(setLoaderText(null));
    }
  };

  const InputLabelStyle = {
    color: customStyles.input.color,
    fontSize: "14px",
    fontWeight: "500",
    mb: "10px",
    fontFamily: customStyles.muiTheme.typography.fontFamily,
  };

  const TextFieldStyle = {
    ".MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${customStyles?.input?.borderColor ?? "#a8a8a8"}`,
      "&:hover": {
        border: `1px solid ${customStyles?.input?.hover?.borderColor}`,
      },
    },
    ".MuiInputBase-root": {
      height: "54px",
      borderRadius: "6px",
      fontWeight: "normal",
      color: customStyles.input.color,
      backgroundColor: customStyles.input.backgroundColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${customStyles?.input?.active?.borderColor}`,
    },
  };

  const CARD_BRAND_OPTIONS = [
    { value: "VISA", label: "Visa" },
    { value: "MASTERCARD", label: "Mastercard" },
    { value: "ELO", label: "Elo" },
  ];

  const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, "0");
    return { value: month, label: month };
  });

  const currentYear = new Date().getFullYear() % 100;
  const YEAR_OPTIONS = Array.from({ length: 20 }, (_, i) => {
    const year = (currentYear + i).toString().padStart(2, "0");
    return { value: year, label: year };
  });

  const INSTALLMENT_OPTIONS = Array.from({ length: 10 }, (_, i) => {
    const installment = i + 1;
    return { value: installment, label: `${installment}x` };
  });

  if (isPaymentFailed) {
    if (isBindFailed) {
      return (
        <>
        <Box
          display="flex"
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
          py={4}
          gap={2}
        >
          {!inspectionData && (
            <>
              <Typography color={"error"} variant="h6" textAlign="center">
                {t("PAYMENT_PAGE_FAILURE_MESSAGE")}
              </Typography>
              <Typography color={"error"} variant="body1" textAlign="center" sx={{ mt: 1 }}>
                Não foi possível efetuar o pagamento. Por favor, verifique os dados fornecidos e tente novamente.
              </Typography>
            </>
          )}

          {isAutoAllianz && inspectionData && (
            <Box
              sx={{
                mt: 2,
                p: 3,
                mb: 4,
                bgcolor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: 2,
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}
              >
                {t("INSPECTION_INFORMATION") || "Inspection Information"}
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: "none", border: "1px solid #dee2e6" }}
              >
                <Table>
                  <TableBody>
                    {Object.entries(inspectionData)
                      .filter(([key]) => key !== "inspectionDocuments") // Filter out inspectionDocuments
                      .map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              borderRight: "1px solid #dee2e6",
                            }}
                          >
                            {key.charAt(0).toUpperCase() +
                              key.slice(1).replace(/([A-Z])/g, " $1")}
                          </TableCell>
                          <TableCell>{String(value || "-")}</TableCell>
                        </TableRow>
                      ))}
                    
                    {/* Add row for PROPOSTA document */}
                    {inspectionData.inspectionDocuments && (() => {
                      const propostaDoc = inspectionData.inspectionDocuments.find(
                        (doc: any) => doc.path_to_file?.includes("PROPOSTA")
                      );
                      
                      return propostaDoc ? (
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              borderRight: "1px solid #dee2e6",
                            }}
                          >
                            Inspection Documents
                          </TableCell>
                          <TableCell>
                            <AnimatedButton
                              variant="contained"
                              size="small"
                              onClick={() => {
                                downloadFile(
                                  propostaDoc.download_url,
                                  `${propostaDoc.file_name || "proposta"}.pdf`
                                );
                              }}
                              sx={{
                                backgroundColor: customStyles?.button?.backgroundColor,
                                color: customStyles?.button?.color,
                                textTransform: "none",
                              }}
                            >
                              Baixar Proposta
                            </AnimatedButton>
                          </TableCell>
                        </TableRow>
                      ) : null;
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {!inspectionData && (
            <Box display="flex" gap={2} mt={2}>
              <AnimatedButton
                variant="contained"
                onClick={handleRetryPayment}
                disabled={isLoading}
                sx={{
                  backgroundColor: customStyles?.button?.backgroundColor,
                  color: customStyles?.button?.color,
                }}
              >
                <Typography sx={{ fontSize: "14px" }}>
                  {t("PAYMENT_PAGE_RETRY_PAYMENT")}
                </Typography>
              </AnimatedButton>

              <AnimatedButton
                variant="outlined"
                onClick={handleExit}
                disabled={isLoading}
                sx={{
                  borderColor: customStyles?.button?.backgroundColor,
                  color: customStyles?.button?.backgroundColor,
                }}
              >
                <Typography sx={{ fontSize: "14px" }}>{t("EXIT")}</Typography>
              </AnimatedButton>
            </Box>
          )}
        </Box>
        <PopupModal
          open={showRetryModal}
          title={getModalContent().title}
          content={getModalContent().content}
          btnText={getModalContent().btnText}
          onClose={() => setShowRetryModal(false)}
          onSave={getModalContent().onSave}
        />
        </>
      );
    }
  }

  return (

      <Formik
        initialValues={getInitialValues()}
        validateOnMount={true}
        validationSchema={validationSchema}
        onSubmit={_onSubmit}
      >
        {({ handleSubmit, errors, touched, values, setFieldValue }) => (
          <Form id="payment-form" onSubmit={handleSubmit}>
            {/* Renewal Information Section */}
            {selectedQuotes &&
              selectedQuotes.length > 0 &&
              selectedQuotes[0]?.data?.allianzRenewal &&
              productSelected !== "MOTO-ALLIANZ" && (
                <Box sx={{ marginBottom: "1.5rem" }}>
                  <RenewalForm errors={errors} touched={touched} />
                </Box>
              )}

            {/* Payment Method Section */}
            {!isHomeownersAllianz && (
              <Box sx={{ marginBottom: "1.5rem" }}>
                <Typography variant="h6" gutterBottom>
                  Forma de Pagamento
                </Typography>
                <Field name="paymentMethod">
                  {({ field }) => (
                    <RadioGroup
                      row
                      {...field}
                      onChange={(e) => {
                        const newMethod = e.target.value as PaymentMethod;
                        setFieldValue("paymentMethod", newMethod);
                        setPaymentMethod(newMethod);
                        // Force blur to release focus after method change
                        setTimeout(() => {
                          (document.activeElement as HTMLElement)?.blur();
                        }, 0);
                      }}
                    >
                      <FormControlLabel
                        value={PaymentMethod.CREDIT_CARD}
                        control={<Radio />}
                        label="Cartão de Crédito"
                      />
                      <FormControlLabel
                        value={PaymentMethod.ACCOUNT_DEBIT}
                        control={<Radio />}
                        label="Débito em Conta"
                      />
                    </RadioGroup>
                  )}
                </Field>
              </Box>
            )}

            {/* Credit Card Section */}
            {values.paymentMethod === PaymentMethod.CREDIT_CARD ? (
              <Box sx={{ marginBottom: "1.5rem" }}>
                <Typography variant="h6" gutterBottom>
                  Dados do Cartão
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <InputLabel sx={InputLabelStyle}>
                      O segurado é o titular do cartão?
                    </InputLabel>
                    <Field name="is_policyholder">
                      {({ field }) => (
                        <RadioGroup
                          row
                          {...field}
                          onChange={(e) => {
                            setFieldValue(
                              "is_policyholder",
                              e.target.value === "true"
                            );
                          }}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 2,
                            marginTop: 1,
                          }}
                        >
                          <FormControlLabel
                            value="true"
                            control={<Radio size="small" />}
                            label="Sim"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "14px",
                                fontWeight: "normal",
                              },
                            }}
                          />
                          <FormControlLabel
                            value="false"
                            control={<Radio size="small" />}
                            label="Não"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "14px",
                                fontWeight: "normal",
                              },
                            }}
                          />
                        </RadioGroup>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel sx={InputLabelStyle}>
                      Nome do titular do cartão
                    </InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="card_owner_name"
                      sx={TextFieldStyle}
                      error={
                        touched.card_owner_name && Boolean(errors.card_owner_name)
                      }
                      helperText={
                        touched.card_owner_name && errors.card_owner_name
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel sx={InputLabelStyle}>CPF/CNPJ</InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="card_owner_cpf_cnpj"
                      sx={TextFieldStyle}
                      error={
                        touched.card_owner_cpf_cnpj &&
                        Boolean(errors.card_owner_cpf_cnpj)
                      }
                      helperText={
                        touched.card_owner_cpf_cnpj && errors.card_owner_cpf_cnpj
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>
                      Bandeira do Cartão
                    </InputLabel>
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      name="card_brand"
                      sx={TextFieldStyle}
                      error={touched.card_brand && Boolean(errors.card_brand)}
                      helperText={touched.card_brand && errors.card_brand}
                    >
                      {CARD_BRAND_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>Número do Cartão</InputLabel>
                    <Field name="card_number">
                      {({ field, form }) => (
                        <TextField
                          {...field}
                          fullWidth
                          sx={TextFieldStyle}
                          error={touched.card_number && Boolean(errors.card_number)}
                          helperText={touched.card_number && errors.card_number}
                          onChange={(e) => {
                            field.onChange(e);
                            // Auto-detect card brand
                            const detectedBrand = detectCardBrand(e.target.value);
                            if (detectedBrand && ['VISA', 'MASTERCARD', 'ELO'].includes(detectedBrand)) {
                              form.setFieldValue('card_brand', detectedBrand);
                            }
                          }}
                          inputProps={{ maxLength: 19 }}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel sx={InputLabelStyle}>Mês de Validade</InputLabel>
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      name="card_exp_month"
                      sx={TextFieldStyle}
                      error={
                        touched.card_exp_month && Boolean(errors.card_exp_month)
                      }
                      helperText={touched.card_exp_month && errors.card_exp_month}
                    >
                      {MONTH_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel sx={InputLabelStyle}>Ano de Validade</InputLabel>
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      name="card_exp_year"
                      sx={TextFieldStyle}
                      error={
                        touched.card_exp_year && Boolean(errors.card_exp_year)
                      }
                      helperText={touched.card_exp_year && errors.card_exp_year}
                    >
                      {YEAR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel sx={InputLabelStyle}>
                      Código de Segurança
                    </InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="card_sec_code"
                      sx={TextFieldStyle}
                      error={
                        touched.card_sec_code && Boolean(errors.card_sec_code)
                      }
                      helperText={touched.card_sec_code && errors.card_sec_code}
                    />
                  </Grid>
                  {(isAutoAllianz || isMotoAllianz) && (
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={InputLabelStyle}>
                        Número de Parcelas
                      </InputLabel>
                      <Field
                        as={TextField}
                        select
                        fullWidth
                        name="installments"
                        sx={TextFieldStyle}
                        error={touched.installments && Boolean(errors.installments)}
                        helperText={touched.installments && errors.installments}
                      >
                        {INSTALLMENT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <>
                <Box sx={{ marginBottom: "1.5rem" }}>
                  <Typography variant="h6" gutterBottom>
                    Dados do Titular da Conta
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <InputLabel sx={InputLabelStyle}>
                        Nome do titular da conta
                      </InputLabel>
                      <Field
                        as={TextField}
                        fullWidth
                        name="nomeTitular"
                        sx={TextFieldStyle}
                        error={touched.nomeTitular && Boolean(errors.nomeTitular)}
                        helperText={touched.nomeTitular && errors.nomeTitular}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={InputLabelStyle}>CPF</InputLabel>
                      <Field
                        as={TextField}
                        fullWidth
                        name="documentoTitular"
                        sx={TextFieldStyle}
                        error={
                          touched.documentoTitular &&
                          Boolean(errors.documentoTitular)
                        }
                        helperText={
                          touched.documentoTitular && errors.documentoTitular
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={InputLabelStyle}>
                        Grau de parentesco
                      </InputLabel>
                      <Field
                        as={TextField}
                        select
                        fullWidth
                        name="parentesco"
                        sx={TextFieldStyle}
                        error={touched.parentesco && Boolean(errors.parentesco)}
                        helperText={touched.parentesco && errors.parentesco}
                      >
                        {PARENTESCO_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>
                    {isHomeownersAllianz && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <InputLabel sx={InputLabelStyle}>
                            Relacionamento com Titular da Conta
                          </InputLabel>
                          <Field
                            as={TextField}
                            select
                            fullWidth
                            name="ownerAccountBankRelationship"
                            sx={TextFieldStyle}
                            error={
                              touched.ownerAccountBankRelationship &&
                              Boolean(errors.ownerAccountBankRelationship)
                            }
                            helperText={
                              touched.ownerAccountBankRelationship &&
                              errors.ownerAccountBankRelationship
                            }
                          >
                            {ACCOUNT_RELATIONSHIP_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InputLabel sx={InputLabelStyle}>
                            Sexo
                          </InputLabel>
                          <Field
                            as={TextField}
                            select
                            fullWidth
                            name="gender"
                            sx={TextFieldStyle}
                            error={
                              touched.gender &&
                              Boolean(errors.gender)
                            }
                            helperText={
                              touched.gender &&
                              errors.gender
                            }
                          >
                            {GENDER_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InputLabel sx={InputLabelStyle}>
                            Data de Nascimento
                          </InputLabel>
                          <Field
                            as={TextField}
                            fullWidth
                            name="dateOfBirth"
                            type="date"
                            sx={TextFieldStyle}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            error={
                              touched.dateOfBirth &&
                              Boolean(errors.dateOfBirth)
                            }
                            helperText={
                              touched.dateOfBirth &&
                              errors.dateOfBirth
                            }
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>

                <Box sx={{ marginBottom: "1.5rem" }}>
                  <Typography variant="h6" gutterBottom>
                    Dados Bancários
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={InputLabelStyle}>
                        Número do Banco
                      </InputLabel>
                      <Field
                        as={TextField}
                        select={shouldUseBankDropdown}
                        fullWidth
                        name="bancoNumero"
                        type={shouldUseBankDropdown ? undefined : "number"}
                        sx={TextFieldStyle}
                        error={touched.bancoNumero && Boolean(errors.bancoNumero)}
                        helperText={touched.bancoNumero && errors.bancoNumero}
                      >
                        {shouldUseBankDropdown &&
                          (isAutoAllianz ? AUTO_ALLIANZ_BANK_OPTIONS : 
                           isMotoAllianz ? MOTO_ALLIANZ_BANK_OPTIONS : 
                           HOME_ALLIANZ_BANK_OPTIONS).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <InputLabel sx={InputLabelStyle}>Agência</InputLabel>
                      <Field
                        as={TextField}
                        fullWidth
                        name="agenciaNumero"
                        type="number"
                        sx={TextFieldStyle}
                        error={
                          touched.agenciaNumero && Boolean(errors.agenciaNumero)
                        }
                        helperText={touched.agenciaNumero && errors.agenciaNumero}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <InputLabel sx={InputLabelStyle}>Dígito Agência</InputLabel>
                      <Field
                        as={TextField}
                        fullWidth
                        name="agenciaDigito"
                        sx={TextFieldStyle}
                        error={
                          touched.agenciaDigito && Boolean(errors.agenciaDigito)
                        }
                        helperText={touched.agenciaDigito && errors.agenciaDigito}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={InputLabelStyle}>Tipo de Conta</InputLabel>
                      <Field
                        as={TextField}
                        select
                        fullWidth
                        name="contaTipo"
                        sx={TextFieldStyle}
                        error={touched.contaTipo && Boolean(errors.contaTipo)}
                        helperText={touched.contaTipo && errors.contaTipo}
                      >
                        {CONTA_TIPO_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <InputLabel sx={InputLabelStyle}>
                        Número da Conta
                      </InputLabel>
                      <Field
                        as={TextField}
                        fullWidth
                        name="contaNumero"
                        sx={TextFieldStyle}
                        error={touched.contaNumero && Boolean(errors.contaNumero)}
                        helperText={touched.contaNumero && errors.contaNumero}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <InputLabel sx={InputLabelStyle}>Dígito</InputLabel>
                      <Field
                        as={TextField}
                        fullWidth
                        name="contaDigito"
                        sx={TextFieldStyle}
                        error={touched.contaDigito && Boolean(errors.contaDigito)}
                        helperText={touched.contaDigito && errors.contaDigito}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={InputLabelStyle}>
                        Dia do Vencimento das Parcelas
                      </InputLabel>
                      <Field
                        as={TextField}
                        fullWidth
                        name="diaPagamento"
                        type="number"
                        inputProps={{ min: 1, max: 31 }}
                        sx={TextFieldStyle}
                        error={
                          touched.diaPagamento && Boolean(errors.diaPagamento)
                        }
                        helperText={touched.diaPagamento && errors.diaPagamento}
                      />
                    </Grid>
                    {(isAutoAllianz || isMotoAllianz) && (
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={InputLabelStyle}>
                          Número de Parcelas
                        </InputLabel>
                        <Field
                          as={TextField}
                          select
                          fullWidth
                          name="installments"
                          sx={TextFieldStyle}
                          error={touched.installments && Boolean(errors.installments)}
                          helperText={touched.installments && errors.installments}
                        >
                          {INSTALLMENT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}

            <Box sx={{ marginBottom: "1.5rem" }}>
              <Typography variant="h6" gutterBottom>
                Endereço
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InputLabel sx={InputLabelStyle}>Tipo de Endereço</InputLabel>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    name="tipoEndereco"
                    sx={TextFieldStyle}
                    error={touched.tipoEndereco && Boolean(errors.tipoEndereco)}
                    helperText={touched.tipoEndereco && errors.tipoEndereco}
                  >
                    {TIPO_ENDERECO_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel sx={InputLabelStyle}>Tipo de Logradouro</InputLabel>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    name="tipoLogradouro"
                    sx={TextFieldStyle}
                    error={
                      touched.tipoLogradouro && Boolean(errors.tipoLogradouro)
                    }
                    helperText={touched.tipoLogradouro && errors.tipoLogradouro}
                  >
                    {TIPO_LOGRADOURO_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel sx={InputLabelStyle}>Logradouro</InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="logradouro"
                    sx={TextFieldStyle}
                    error={touched.logradouro && Boolean(errors.logradouro)}
                    helperText={touched.logradouro && errors.logradouro}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel sx={InputLabelStyle}>Número</InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="numero"
                    sx={TextFieldStyle}
                    error={touched.numero && Boolean(errors.numero)}
                    helperText={touched.numero && errors.numero}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel sx={InputLabelStyle}>CEP</InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="cep"
                    sx={TextFieldStyle}
                    error={touched.cep && Boolean(errors.cep)}
                    helperText={touched.cep && errors.cep}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel sx={InputLabelStyle}>Bairro</InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="bairro"
                    sx={TextFieldStyle}
                    error={touched.bairro && Boolean(errors.bairro)}
                    helperText={touched.bairro && errors.bairro}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{ marginTop: "40px" }}
              onMouseDown={(e) => {
                // Check if clicking on the submit button area
                const target = e.target as HTMLElement;
                const isSubmitButton = target.closest('[data-cy="payment-footer-make-payment-btn"]');

                if (isSubmitButton && !isLoading) {
                  // Prevent the mousedown from causing blur issues
                  e.preventDefault();
                  // Blur any focused input
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                  // Call handleSubmit directly from mouseDown to avoid click being consumed by blur
                  setTimeout(() => {
                    handleSubmit();
                  }, 10);
                }
              }}
            >
              <PaymentFooter
                backButtonProps={{
                  disabled: isLoading,
                  onClickCB: onCancel,
                  btnText: t("BACK"),
                }}
                submitButtonProps={{
                  disabled: isLoading,
                  btnText: t("PAYMENT_PAGE_MAKE_PAYMENT"),
                  onClickCB: () => {
                  },
                }}
                isLoading={isLoading}
              />
            </Box>
          </Form>
        )}
      </Formik>

  );
};

export default CustomPayment;
